import React, { useCallback, useState } from 'react';
import { Form } from '@remix-run/react';
import { TentTypeV1, TentTypeV2 } from '@camp67/model';
import { parseDate, bathingDates } from '@camp67/model/booking/dates';
import clsx from 'clsx';

import { reservationStore } from '../../state/reservation-store';
import { useReservationStore } from '../../state/use-reservation-store';
import { trackButtonClick, ButtonTag, Brand } from '../../state/analytics';
import { PackageToggle, PackageToggleMultiple } from '../PackageToggle';
import type { NivaasPackage } from '../../types/package';
import { packagesState } from '../../state/packages';

import {
  BookingFormSection,
  BookingFormSectionMobile,
  Step,
  BookingFormSectionMessages,
} from './booking-form-section';
import {
  TentExperienceCarousel,
  TentCarouselItem,
  TentExperienceCarouselMessages,
} from './tent-experience-carousel';
import {
  TentExperienceGalleryItem,
  TentGalleryItem,
  TentExperienceGalleryItemMessages,
} from './tent-experience-gallery-item';
import {
  SubmissionCTA,
  SubmissionCTAMobile,
  SubmissionCTAMessages,
  MetadataIconType,
} from './submission-cta';
import { DateFieldSingle } from '../form-fields/date-field-single';
import { DateField } from '../form-fields/date-field';
import { NumberField } from '../form-fields/number-field';
import { GuestList } from '../form-fields/guest-list';
import { PrimaryContact } from '../form-fields/primary-contact';
import { TextField } from '../form-fields/text-field';
import { PhoneField } from '../form-fields/phone-field';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const goToElement = (id: Step) => {
  delay(10).then(() => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth' });
  });
};

/**
 * Full tent configuration combining carousel and gallery item data
 */
export interface TentConfig {
  tentType: TentTypeV1 | TentTypeV2;
  imageUrl: string;
  imageAlt: string;
  title: string;
  displayName?: React.ReactNode;
  description: string;
  gallery: Array<{ imageUrl: string }>;
  amenities: Array<{ summary: string; detail?: React.ReactNode }>;
  /** Whether this tent has a private bathroom (vs shared) */
  hasPrivateBathroom?: boolean;
}

/**
 * All messages needed for the booking form
 */
export interface BookingFormMessages {
  // Form ID
  formId: string;

  // Section titles (use React.ReactNode for i18n with bold/styled text)
  step1Title: React.ReactNode;
  step2Title: React.ReactNode;
  step3Title: React.ReactNode;

  // Section messages
  section: BookingFormSectionMessages;

  // Carousel messages
  carousel: TentExperienceCarouselMessages;

  // Gallery item messages
  galleryItem: TentExperienceGalleryItemMessages;

  // Submission CTA messages
  submissionCta: SubmissionCTAMessages;

  // Step 2 labels
  checkInLabel: string;
  checkOutLabel: string;
  adultsLabel: string;
  childrenLabel: string;
  childrenDetail: string;

  // Step 3 labels
  guestsTitle: string;
  guestsDisclaimer: React.ReactNode;
  primaryContactTitle: string;
  primaryContactInfo: React.ReactNode;
  emailPlaceholder: string;
  phonePlaceholder: string;

  // Guest list messages
  guestList: {
    tooManyGuests: (totalTentCapacity: number, guestCount: number) => React.ReactNode;
    additionalBed: string;
    enterName: string;
    enterAge: string;
    selectGender: string;
    genderMale: string;
    genderFemale: string;
    genderNonBinary: string;
    genderPreferNotToSay: string;
  };

  // Primary contact messages
  primaryContact: {
    primaryContactLabel: string;
    selectPrimaryContact: string;
  };

  // Package selection messages (optional - only shown if packages available)
  packages?: {
    title: string;
    roomOnlyLabel: string;
    roomOnlyDescription: string;
    noPackagesAvailable: string;
  };

  // Experience selector messages (for the hero package selection)
  experienceSelector?: {
    title: string;
    subtitle: string;
    roomOnlyTitle: string;
    roomOnlySubtitle: string;
    roomOnlyDescription: string;
    roomOnlyFeatures: string[];
  };
}

/**
 * Routes used by the booking form
 */
export interface BookingFormRoutes {
  completePayment: string;
  paymentPolicy: string;
  privacyPolicy: string;
}

export interface BookingFormProps {
  /** Brand identifier */
  brand: Brand;
  /** Tent configurations to display */
  tents: TentConfig[];
  /** All messages for i18n */
  messages: BookingFormMessages;
  /** Routes for navigation */
  routes: BookingFormRoutes;
  /** Optional logo URL for submission CTA */
  logoUrl?: string;
  logoAlt?: string;
  /** Called when navigating to payment */
  onNavigateToPayment?: () => void;
  /** Optional custom icon renderer for metadata icons (e.g., to use sprites) */
  renderIcon?: (icon: MetadataIconType) => React.ReactNode;
  /** Custom CSS class for the CTA button background (e.g., 'bg-emerald-600' for KS) */
  ctaButtonClassName?: string;
  /** Custom CSS class for the CTA button shadow */
  ctaShadowClassName?: string;
}

/**
 * Main booking form component
 */
export const BookingForm: React.FC<BookingFormProps> = (props) => {
  useReservationStore(props.messages.formId);

  const [activeTent, setActiveTent] = useState<TentTypeV1 | TentTypeV2>(
    props.tents[0]?.tentType ?? ('' as TentTypeV1),
  );

  const onTentChange = useCallback(
    (type: TentTypeV1 | TentTypeV2, operation: 'add' | 'subtract') => {
      if (operation === 'add') {
        reservationStore.addRoom(type);
        trackButtonClick(ButtonTag.ADD_ACCOMMODATION, { tentType: type });
      } else {
        reservationStore.removeRoom(type);
        trackButtonClick(ButtonTag.SUBTRACT_ACCOMMODATION, { tentType: type });
      }
    },
    [],
  );

  const onClickToPayment = useCallback(() => {
    const isValid = reservationStore.validate();
    if (!isValid) {
      return;
    }
    props.onNavigateToPayment?.();
  }, [props]);

  return (
    <>
      <div className="md:hidden h-full">
        <BookingFormMobile
          {...props}
          activeTent={activeTent}
          onTentTypeChange={setActiveTent}
          onTentChange={onTentChange}
          onClickToPayment={onClickToPayment}
        />
      </div>
      <div className="hidden md:block h-full">
        <BookingFormDesktop
          {...props}
          activeTent={activeTent}
          onTentTypeChange={setActiveTent}
          onTentChange={onTentChange}
          onClickToPayment={onClickToPayment}
        />
      </div>
    </>
  );
};

interface BookingFormInnerProps extends BookingFormProps {
  activeTent: TentTypeV1 | TentTypeV2;
  onTentTypeChange: (type: TentTypeV1 | TentTypeV2) => void;
  onTentChange: (type: TentTypeV1 | TentTypeV2, operation: 'add' | 'subtract') => void;
  onClickToPayment: VoidFunction;
  ctaButtonClassName?: string;
  ctaShadowClassName?: string;
}

const handleDateStartChange = (from: Date | undefined): void => {
  reservationStore.chooseDateStart(from);
};

const handleDateChange = (_from: Date | undefined, to: Date | undefined): void => {
  reservationStore.chooseDateRange(to);
};

/**
 * Desktop version of the booking form
 */
const BookingFormDesktop: React.FC<BookingFormInnerProps> = ({
  tents,
  messages,
  activeTent,
  onTentTypeChange,
  onTentChange,
  onClickToPayment,
  logoUrl,
  logoAlt,
  renderIcon,
  ctaButtonClassName,
  ctaShadowClassName,
}) => {
  const reservation = reservationStore.state.reservation;
  const bookingStartDate = reservation.startDate;
  const bookingEndDate = reservation.endDate;
  const primaryContact = reservationStore.state.primaryContact;
  const hasValidGuests = reservationStore.validateGuests();
  const price = reservationStore.cost;

  const isStep1Complete = reservationStore.isStep1Complete();
  const isStep2Complete = reservationStore.isStep2Complete();

  // Package selection - get all packages for experience selector at top of Step 1
  const selectedPackage = reservationStore.getPackage();
  const allPackages = packagesState.packages;
  const hasPackages = allPackages.length > 0;

  const onPackageChange = useCallback((pkg: NivaasPackage | null) => {
    reservationStore.setPackage(pkg);
    trackButtonClick(ButtonTag.SELECT_PACKAGE, { packageId: pkg?.id ?? 'room-only' });
  }, []);

  const [stepsExpanded, setStepsExpanded] = useState({
    'step-1': true,
    'step-2': false,
    'step-3': false,
  });

  const onExpand = useCallback(
    (step: Step) => () => {
      setStepsExpanded((prev) => ({ ...prev, [step]: !prev[step] }));
    },
    [],
  );

  const onNext = useCallback((step: Step) => {
    if (step === 'step-1') {
      setStepsExpanded((prev) => ({ ...prev, [step]: false, 'step-2': true }));
      goToElement('step-2');
    } else if (step === 'step-2') {
      setStepsExpanded((prev) => ({ ...prev, [step]: false, 'step-3': true }));
      goToElement('step-3');
    }
  }, []);

  // Convert tents to carousel and gallery formats
  const carouselItems: TentCarouselItem[] = tents.map((t) => ({
    tentType: t.tentType,
    imageUrl: t.imageUrl,
    imageAlt: t.imageAlt,
    title: t.title,
  }));

  const activeTentConfig = tents.find((t) => t.tentType === activeTent);
  const galleryItem: TentGalleryItem | null = activeTentConfig
    ? {
        tentType: activeTentConfig.tentType,
        title: activeTentConfig.title,
        displayName: activeTentConfig.displayName,
        description: activeTentConfig.description,
        imageAlt: activeTentConfig.imageAlt,
        gallery: activeTentConfig.gallery,
        amenities: activeTentConfig.amenities,
        hasPrivateBathroom: activeTentConfig.hasPrivateBathroom,
      }
    : null;

  // Create a function to get tent config for submission CTA gallery
  const getTentConfig = useCallback(
    (type: TentTypeV1 | TentTypeV2) => {
      const tent = tents.find((t) => t.tentType === type);
      if (!tent) return null;
      return {
        imageUrl: tent.imageUrl,
        imageAlt: tent.imageAlt,
        title: tent.title,
      };
    },
    [tents],
  );

  return (
    <Form
      id={messages.formId}
      className="w-full sm:pt-6 mb-36 mt-4 grid md:grid-cols-[72.5%_27.5%] gap-8 relative z-20"
    >
      <div className="w-full">
        {/* Step 1: Select accommodation */}
        <fieldset id="step-1" className="flex flex-col w-full mt-6 first-of-type:mt-0">
          <BookingFormSection
            isStepDisabled={false}
            showExpanded={stepsExpanded['step-1']}
            step="step-1"
            title={messages.step1Title}
            messages={messages.section}
            onExpand={onExpand('step-1')}
            onNext={onNext}
          >
            {tents.length > 0 ? (
              <>
                <TentExperienceCarousel
                  tents={carouselItems}
                  activeTent={activeTent}
                  countProvider={reservationStore.getRoomCount}
                  onTentChange={onTentTypeChange}
                  messages={messages.carousel}
                />
                {galleryItem && (
                  <TentExperienceGalleryItem
                    tent={galleryItem}
                    currentCount={reservationStore.getRoomCount(activeTent)}
                    onClick={onTentChange}
                    messages={messages.galleryItem}
                  />
                )}
                {/* Package Selection - compact toggle shown after room selection */}
                {hasPackages && (
                  <div className="px-6 pt-4 pb-2">
                    {allPackages.length === 1 ? (
                      <PackageToggle
                        selectedPackage={selectedPackage}
                        onSelectPackage={onPackageChange}
                        packages={allPackages}
                      />
                    ) : (
                      <PackageToggleMultiple
                        selectedPackage={selectedPackage}
                        onSelectPackage={onPackageChange}
                        packages={allPackages}
                      />
                    )}
                  </div>
                )}
              </>
            ) : null}
          </BookingFormSection>
        </fieldset>

        {/* Step 2: Dates and guests */}
        <fieldset id="step-2" className="flex flex-col w-full mt-6">
          <BookingFormSection
            isStepDisabled={!isStep1Complete}
            showExpanded={isStep1Complete && stepsExpanded['step-2']}
            step="step-2"
            title={messages.step2Title}
            messages={messages.section}
            onExpand={onExpand('step-2')}
            onNext={onNext}
          >
            <div className="flex flex-col md:flex-row relative w-full md:gap-5 px-6">
              <DateFieldSingle
                isRequired
                label={messages.checkInLabel}
                min={reservationStore.getStartOfBookingTimeRange()}
                max={reservationStore.getEndOfBookingTimeRange()}
                onChange={handleDateStartChange}
                value={bookingStartDate ? parseDate(bookingStartDate) : undefined}
                availableDates={reservationStore.getAvailableDates()}
                tents={reservation.rooms as [TentTypeV1, number][]}
                clearEndDate={(f) => handleDateChange(f, undefined)}
                bathingDates={bathingDates}
              />
              <DateField
                isRequired
                label={messages.checkOutLabel}
                min={
                  bookingStartDate
                    ? parseDate(bookingStartDate)
                    : reservationStore.getStartOfBookingTimeRange()
                }
                max={reservationStore.getEndOfBookingTimeRange()}
                onChange={handleDateChange}
                value={{
                  from: bookingStartDate ? parseDate(bookingStartDate) : undefined,
                  to: bookingEndDate ? parseDate(bookingEndDate) : undefined,
                }}
                tents={reservation.rooms as [TentTypeV1, number][]}
                availableDates={reservationStore.getAvailableDates()}
                bathingDates={bathingDates}
              />
            </div>
            <div className="flex flex-col md:flex-row w-full md:gap-5 px-6">
              <NumberField
                isRequired
                type="adult"
                label={messages.adultsLabel}
                value={reservation.guests.adult}
                onChange={reservationStore.setAdultCount}
                name="adult"
              />
              <NumberField
                type="children"
                label={messages.childrenLabel}
                detail={messages.childrenDetail}
                value={reservation.guests.children}
                onChange={reservationStore.setChildCount}
                name="children"
              />
            </div>
          </BookingFormSection>
        </fieldset>

        {/* Step 3: Guest details */}
        <fieldset id="step-3" className="flex flex-col w-full mt-6">
          <BookingFormSection
            isStepDisabled={!isStep2Complete}
            step="step-3"
            showExpanded={isStep2Complete && stepsExpanded['step-3']}
            title={messages.step3Title}
            messages={messages.section}
            onExpand={onExpand('step-3')}
            onNext={onClickToPayment}
          >
            <div className="px-6 pt-5">
              <p className="font-bold">{messages.guestsTitle}</p>
              <p className="text-gray-200">{messages.guestsDisclaimer}</p>
              <GuestList
                totalTentCapacity={reservationStore.getCapacityOfRooms()}
                guestCount={reservationStore.getGuestCount()}
                guests={reservation.guests.details}
                primaryContactId={primaryContact.id}
                onGuestChange={reservationStore.updateGuestDetail}
                messages={messages.guestList}
              />
              <div
                className={clsx({
                  'opacity-50 pointer-events-none': !hasValidGuests,
                  'opacity-100': hasValidGuests,
                })}
              >
                <p className="font-bold mt-8">{messages.primaryContactTitle}</p>
                <p className="text-gray-200">{messages.primaryContactInfo}</p>
                <PrimaryContact
                  guests={reservation.guests.details}
                  onChange={reservationStore.choosePrimaryContact}
                  primaryContact={primaryContact}
                  messages={messages.primaryContact}
                />
                <div className="flex flex-col md:grid md:grid-cols-2 w-full md:gap-5">
                  <TextField
                    type="email"
                    isRequired
                    placeholder={messages.emailPlaceholder}
                    error={reservationStore.getError('email')}
                    onChange={reservationStore.setPrimaryEmail}
                    name="email"
                    value={primaryContact.email}
                  />
                  <PhoneField
                    name="phone"
                    onChangeCode={reservationStore.setCountryCode}
                    onChangeNumber={reservationStore.setPrimaryPhoneNumber}
                    countryCode={reservationStore.getCountryCode()}
                    phoneNumber={reservationStore.getPhoneNumber()}
                    placeholder={messages.phonePlaceholder}
                    error={reservationStore.getError('phone')}
                  />
                </div>
              </div>
            </div>
          </BookingFormSection>
        </fieldset>
      </div>

      <SubmissionCTA
        total={price.total}
        totalWithGst={price.totalWithGst}
        totalGst={price.totalGst}
        gstPercentage={price.gstPercentage}
        nights={price.nights}
        hasErrors={reservationStore.hasErrors()}
        canSubmit={reservationStore.canSubmit}
        onClick={onClickToPayment}
        reservation={reservation}
        messages={messages.submissionCta}
        logoUrl={logoUrl}
        logoAlt={logoAlt}
        getTentConfig={getTentConfig}
        renderIcon={renderIcon}
        ctaButtonClassName={ctaButtonClassName}
        ctaShadowClassName={ctaShadowClassName}
      />
    </Form>
  );
};

/**
 * Mobile version of the booking form
 */
const BookingFormMobile: React.FC<BookingFormInnerProps> = ({
  tents,
  messages,
  activeTent,
  onTentTypeChange,
  onTentChange,
  onClickToPayment,
  ctaButtonClassName,
}) => {
  const reservation = reservationStore.state.reservation;
  const bookingStartDate = reservation.startDate;
  const bookingEndDate = reservation.endDate;
  const primaryContact = reservationStore.state.primaryContact;
  const hasValidGuests = reservationStore.validateGuests();
  const price = reservationStore.cost;

  const isStep1Complete = reservationStore.isStep1Complete();
  const isStep2Complete = reservationStore.isStep2Complete();

  // Package selection - get all packages for experience selector at top of Step 1
  const selectedPackage = reservationStore.getPackage();
  const allPackages = packagesState.packages;
  const hasPackages = allPackages.length > 0;


  const onPackageChange = useCallback((pkg: NivaasPackage | null) => {
    reservationStore.setPackage(pkg);
    trackButtonClick(ButtonTag.SELECT_PACKAGE, { packageId: pkg?.id ?? 'room-only' });
  }, []);

  const [stepsExpanded, setStepsExpanded] = useState({
    'step-1': true,
    'step-2': true,
    'step-3': true,
  });

  const onExpand = useCallback(
    (step: Step) => () => {
      setStepsExpanded((prev) => ({ ...prev, [step]: !prev[step] }));
    },
    [],
  );

  const onNext = useCallback((step: Step) => {
    if (step === 'step-1') {
      setStepsExpanded((prev) => ({ ...prev, [step]: false, 'step-2': true }));
      goToElement('step-2');
    } else if (step === 'step-2') {
      setStepsExpanded((prev) => ({ ...prev, [step]: false, 'step-3': true }));
      goToElement('step-3');
    }
  }, []);

  // Convert tents to carousel and gallery formats
  const carouselItems: TentCarouselItem[] = tents.map((t) => ({
    tentType: t.tentType,
    imageUrl: t.imageUrl,
    imageAlt: t.imageAlt,
    title: t.title,
  }));

  const activeTentConfig = tents.find((t) => t.tentType === activeTent);
  const galleryItem: TentGalleryItem | null = activeTentConfig
    ? {
        tentType: activeTentConfig.tentType,
        title: activeTentConfig.title,
        displayName: activeTentConfig.displayName,
        description: activeTentConfig.description,
        imageAlt: activeTentConfig.imageAlt,
        gallery: activeTentConfig.gallery,
        amenities: activeTentConfig.amenities,
        hasPrivateBathroom: activeTentConfig.hasPrivateBathroom,
      }
    : null;

  return (
    <Form id={messages.formId} className="w-full h-full gap-8 relative z-20">
      <div className="w-full">
        {/* Step 1 */}
        <fieldset id="step-1" className="flex flex-col w-full">
          <BookingFormSectionMobile
            isStepDisabled={false}
            showExpanded={stepsExpanded['step-1']}
            step="step-1"
            title={messages.step1Title}
            messages={messages.section}
            onExpand={onExpand('step-1')}
            onNext={onNext}
          >
            {tents.length > 0 ? (
              <>
                <TentExperienceCarousel
                  tents={carouselItems}
                  activeTent={activeTent}
                  countProvider={reservationStore.getRoomCount}
                  onTentChange={onTentTypeChange}
                  messages={messages.carousel}
                />
                {galleryItem && (
                  <TentExperienceGalleryItem
                    tent={galleryItem}
                    currentCount={reservationStore.getRoomCount(activeTent)}
                    onClick={onTentChange}
                    messages={messages.galleryItem}
                  />
                )}
                {/* Package Selection - compact toggle shown after room selection */}
                {hasPackages && (
                  <div className="px-4 pt-4 pb-2">
                    {allPackages.length === 1 ? (
                      <PackageToggle
                        selectedPackage={selectedPackage}
                        onSelectPackage={onPackageChange}
                        packages={allPackages}
                      />
                    ) : (
                      <PackageToggleMultiple
                        selectedPackage={selectedPackage}
                        onSelectPackage={onPackageChange}
                        packages={allPackages}
                      />
                    )}
                  </div>
                )}
              </>
            ) : null}
          </BookingFormSectionMobile>
        </fieldset>

        {/* Step 2 */}
        <fieldset id="step-2" className="flex flex-col w-full relative z-30">
          <BookingFormSectionMobile
            isStepDisabled={!isStep1Complete}
            showExpanded={isStep1Complete && stepsExpanded['step-2']}
            step="step-2"
            title={messages.step2Title}
            messages={messages.section}
            onExpand={onExpand('step-2')}
            onNext={onNext}
          >
            <div className="flex flex-col w-full px-6 relative z-30">
              <DateFieldSingle
                isRequired
                isMobile
                label={messages.checkInLabel}
                min={reservationStore.getStartOfBookingTimeRange()}
                max={reservationStore.getEndOfBookingTimeRange()}
                onChange={handleDateStartChange}
                value={bookingStartDate ? parseDate(bookingStartDate) : undefined}
                availableDates={reservationStore.getAvailableDates()}
                tents={reservation.rooms as [TentTypeV1, number][]}
                clearEndDate={(f) => handleDateChange(f, undefined)}
                bathingDates={bathingDates}
              />
              <DateField
                isRequired
                isMobile
                label={messages.checkOutLabel}
                min={
                  bookingStartDate
                    ? parseDate(bookingStartDate)
                    : reservationStore.getStartOfBookingTimeRange()
                }
                max={reservationStore.getEndOfBookingTimeRange()}
                onChange={handleDateChange}
                value={{
                  from: bookingStartDate ? parseDate(bookingStartDate) : undefined,
                  to: bookingEndDate ? parseDate(bookingEndDate) : undefined,
                }}
                tents={reservation.rooms as [TentTypeV1, number][]}
                availableDates={reservationStore.getAvailableDates()}
                bathingDates={bathingDates}
              />
            </div>
            <div className="flex flex-col w-full px-6">
              <NumberField
                isRequired
                type="adult"
                label={messages.adultsLabel}
                value={reservation.guests.adult}
                onChange={reservationStore.setAdultCount}
                name="adult"
              />
              <NumberField
                type="children"
                label={messages.childrenLabel}
                detail={messages.childrenDetail}
                value={reservation.guests.children}
                onChange={reservationStore.setChildCount}
                name="children"
              />
            </div>
          </BookingFormSectionMobile>
        </fieldset>

        {/* Step 3 */}
        <fieldset id="step-3" className="flex flex-col w-full pb-12">
          <BookingFormSectionMobile
            isStepDisabled={!isStep2Complete}
            step="step-3"
            showExpanded={isStep2Complete && stepsExpanded['step-3']}
            title={messages.step3Title}
            messages={messages.section}
            onExpand={onExpand('step-3')}
            onNext={onClickToPayment}
          >
            <div className="px-6 pt-5">
              <p className="font-bold">{messages.guestsTitle}</p>
              <p className="text-gray-200">{messages.guestsDisclaimer}</p>
              <GuestList
                totalTentCapacity={reservationStore.getCapacityOfRooms()}
                guestCount={reservationStore.getGuestCount()}
                guests={reservation.guests.details}
                primaryContactId={primaryContact.id}
                onGuestChange={reservationStore.updateGuestDetail}
                messages={messages.guestList}
              />
              <div
                className={clsx({
                  'opacity-50 pointer-events-none': !hasValidGuests,
                  'opacity-100': hasValidGuests,
                })}
              >
                <p className="font-bold mt-8">{messages.primaryContactTitle}</p>
                <p className="text-gray-200">{messages.primaryContactInfo}</p>
                <PrimaryContact
                  guests={reservation.guests.details}
                  onChange={reservationStore.choosePrimaryContact}
                  primaryContact={primaryContact}
                  messages={messages.primaryContact}
                />
                <div className="flex flex-col w-full">
                  <TextField
                    type="email"
                    isRequired
                    placeholder={messages.emailPlaceholder}
                    error={reservationStore.getError('email')}
                    onChange={reservationStore.setPrimaryEmail}
                    name="email"
                    value={primaryContact.email}
                  />
                  <PhoneField
                    name="phone"
                    onChangeCode={reservationStore.setCountryCode}
                    onChangeNumber={reservationStore.setPrimaryPhoneNumber}
                    countryCode={reservationStore.getCountryCode()}
                    phoneNumber={reservationStore.getPhoneNumber()}
                    placeholder={messages.phonePlaceholder}
                    error={reservationStore.getError('phone')}
                  />
                </div>
              </div>
            </div>
          </BookingFormSectionMobile>
        </fieldset>
      </div>

      <SubmissionCTAMobile
        total={price.total}
        totalWithGst={price.totalWithGst}
        totalGst={price.totalGst}
        gstPercentage={price.gstPercentage}
        nights={price.nights}
        hasErrors={reservationStore.hasErrors()}
        canSubmit={reservationStore.canSubmit}
        onClick={onClickToPayment}
        reservation={reservation}
        messages={messages.submissionCta}
        ctaButtonClassName={ctaButtonClassName}
      />
    </Form>
  );
};
